import { t } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import {
  ToggleGroup,
  ToggleGroupItem,
} from 'designSystem/components/ToggleGroup'
import { ChartTooltip } from 'features/timeSeries/components/ChartTooltip'
import { useScatterplotContext } from 'features/timeSeries/ScatterplotContext'
import { isFiniteNumber } from 'features/timeSeries/utils/axisRangeHelpers'
import React from 'react'
import styled from 'styled-components'
import { updateColorOpacity } from 'utils/colorHelpers'

/**
 * One control row: MODE toggle, Y-SCALE toggle, Auto. Y min/max editing lives
 * exclusively in the pens table below (editable in both scale modes), so the
 * bar's content is identical in both modes and toggling Single/Multi never
 * shifts the layout.
 */
const ControlsBar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem 0.625rem;
  padding: 0.375rem 0.25rem;
  font-size: 0.6875rem;
`

const ControlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  white-space: nowrap;
`

/**
 * The shared ToggleGroup's default active state (bg-muted) is too subtle in
 * the dark theme, so the selected segment gets the primary-tinted fill +
 * primary ring the chart controls already use for "custom range active" (see
 * ActiveTimeRangeSectionWithPopover); unselected segments dim to secondary
 * text. Local override only — other ToggleGroup consumers keep the
 * design-system look.
 */
const SegmentItem = styled(ToggleGroupItem)`
  padding: 0 0.5rem;
  color: ${({ theme }) => theme.forecast.secondaryText};

  &:hover {
    color: ${({ theme }) => theme.forecast.defaultText};
  }

  &[data-state='on'] {
    background-color: ${({ theme }) =>
      updateColorOpacity(theme.forecast.primary ?? '#007BFF', 0.22)};
    box-shadow: inset 0 0 0 1px
      ${({ theme }) => theme.forecast.primary ?? '#007BFF'};
    color: ${({ theme }) => theme.forecast.activeText};
  }
`

export const formatAutoRangeValue = (value: number): string =>
  Number(value.toPrecision(4)).toString()

/**
 * The placeholder an empty (auto) min/max field shows: the computed auto value
 * when one exists; a neutral dash while data is still loading; "No data" only
 * once loading has settled with no finite points.
 */
export const getAutoRangePlaceholder = (
  autoValue: number | undefined,
  rangesSettled: boolean
): string => {
  if (isFiniteNumber(autoValue)) {
    return formatAutoRangeValue(autoValue)
  }
  return rangesSettled ? t`No data` : '—'
}

/**
 * Scale-mode controls at the top of the pens table.
 *
 * Y min/max values are edited in the table's Y Min/Y Max columns in BOTH
 * modes. In single mode a committed cell edit fans out to every pen (see
 * applyAxisBoundEdit), so all rows updating together is the visual cue that
 * the scale is shared. The Auto button clears every custom range so all pens
 * auto-scale.
 */
interface YScaleControlsProps {
  /** Surfaces that re-home the auto-scale action (the workbench pane header). */
  hideAuto?: boolean
}

export const YScaleControls: React.FC<YScaleControlsProps> = ({
  hideAuto = false,
}) => {
  const { t } = useLingui()

  const {
    singleScaleMode,
    handleScaleModeToggle,
    autoScaleMode,
    handleAutoScaleAll,
    connectPoints,
    setConnectPoints,
  } = useScatterplotContext()

  return (
    <ControlsBar>
      <ControlGroup>
        <ToggleGroup
          type='single'
          variant='outline'
          size='sm'
          aria-label={t`Chart mode`}
          value={connectPoints ? 'line' : 'scatter'}
          onValueChange={(value) => {
            // Radix emits '' when the active segment is re-clicked; one mode
            // is always selected, so ignore deselection.
            if (value === 'scatter' || value === 'line') {
              setConnectPoints(value === 'line')
            }
          }}>
          <SegmentItem value='scatter'>
            <Trans>Scatter</Trans>
          </SegmentItem>
          <SegmentItem value='line'>
            <Trans>Line</Trans>
          </SegmentItem>
        </ToggleGroup>
      </ControlGroup>
      <ControlGroup>
        <ToggleGroup
          type='single'
          variant='outline'
          size='sm'
          aria-label={t`Y-scale mode`}
          value={singleScaleMode ? 'single' : 'multi'}
          onValueChange={(value) => {
            if (value === 'single' || value === 'multi') {
              handleScaleModeToggle(value === 'single')
            }
          }}>
          <SegmentItem value='single'>
            <Trans>Single Y-Scale</Trans>
          </SegmentItem>
          <SegmentItem value='multi'>
            <Trans>Multi Y-Scale</Trans>
          </SegmentItem>
        </ToggleGroup>
      </ControlGroup>
      {/* Pinned to the row's right edge, clear of the toggles. The tooltip's
          wrapper span is the actual flex item, so the pin must live on it —
          a margin on the Button itself would be defeated by the wrapper. */}
      {!hideAuto && (
        <ChartTooltip
          label={t`Clear all custom ranges and auto-scale every pen`}
          side='top'
          style={{ marginLeft: 'auto' }}>
          <Button
            variant='outline'
            size='sm'
            disabled={autoScaleMode}
            onClick={handleAutoScaleAll}>
            <Trans>Auto</Trans>
          </Button>
        </ChartTooltip>
      )}
    </ControlsBar>
  )
}
